import { createContext, useContext, useState } from "react";
import { useCallback } from "react";
import { useEffect } from "react";
import SpinnerComponent from "../../Components/Loding/Spinner";
import { GetAtendimentoAssistenciaTecnicaController } from "../../Controllers/AssistenciaTecnica/Atendimento/AtendimentoControllers";
import { GetAtendimentoClienteController } from "../../Controllers/AtendimentoCliente/Atendimento/AtendimentoControllers";
import { AtendimentoController } from "../../Controllers/Atendimentos/Atendimento";
import {
    IAtendimento,
    IAtendimentoGenericoVendas,
} from "../../Interfaces/Geral/Atendimento/AtendimentoInterfaces";
import { TModulo } from "../../Interfaces/Utils/Modulo/ModuloInterfaces";
import InternalError from "../../Pages/Errors/InternalError";
import { ACESSOS } from "../../Routes/Acessos";

import { HistoricoProvider } from "../Historico";
import { useModuloInfo } from "../ModuloInfo";
import { getInforVendasController } from "../../Controllers/Geral/InformacoesVenda/InformacoesVendaControllers";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import { GetClienteCompleto } from "../../Controllers/Geral/GestaoClientes/GestaoClientesController";
import { IClienteCompleto } from "../../Interfaces/Geral/Cliente/IClienteSimples";
import { DadosOportunidadeLeadController } from "../../Controllers/Vendas/Oportunidade/OportunidadeController";
import { IDadosOportunidade } from "../../Interfaces/Vendas/Oportunidade/OportunidadeInterface";

type IAtendimentoModulos = Extract<
    TModulo,
    "Cobranca" | "Atendimento" | "Assistencia"
>;
interface IAtendimentoPageProviderProps {
    children: JSX.Element | JSX.Element[];
    ID_CASO: number;
}
interface IAtendimentoPageContext {
    Atendimento: IAtendimento | undefined;
    listenDocChanges: boolean;
    updateAtendimento: () => void;
    updateDoc: () => void;
    QtdOutrosProdutosInadimplentes: number;
    setQtdOutrosProdutosInadimplentes: (c: number) => void;
    TIPO_CASO: IAtendimentoModulos;
    bloquearInteracoes: boolean;
    idModulo: number;
    ID_CASO: number;
    lstInforVendas?: IAtendimentoGenericoVendas | undefined;
    getInforVendas?: (
        ...args: any[]
    ) => Promise<IAtendimentoGenericoVendas | undefined>;
    Cliente: IClienteCompleto | undefined;
    ClienteLoading: boolean;
    GetCliente: (...args: any[]) => Promise<IClienteCompleto | undefined>;

    dadosOportunidade: IDadosOportunidade | undefined;
    dadosOportunidadeLoading: boolean;
}

const CASO = {
    Cobranca: {
        getCaso: AtendimentoController,
        id: ACESSOS.COBRANCA.ID_MODULO,
    },
    Atendimento: {
        getCaso: GetAtendimentoClienteController,
        id: ACESSOS.ATENDIMENTO.ID_MODULO,
    },
    Assistencia: {
        getCaso: GetAtendimentoAssistenciaTecnicaController,
        id: ACESSOS.ASSISTENCIA.ID_MODULO,
    },
    Vendas: {
        getCaso: GetAtendimentoClienteController,
        /**@todo trocar o id módulo para o do vendas quando o método do backend de listar produtos estiver adicionado o caso para o id do vendas */
        id: ACESSOS.VENDAS.ID_MODULO,
    },
    EntregaChaves: {
        getCaso: GetAtendimentoAssistenciaTecnicaController,
        id: ACESSOS.ENTREGA_DE_CHAVES.ID_MODULO,
    },
    Repasse: {
        getCaso: GetAtendimentoClienteController,
        id: ACESSOS.REPASSE.ID_MODULO,
    },
};

const AtendimentoPageContext = createContext<IAtendimentoPageContext>(
    {} as IAtendimentoPageContext
);

export function AtendimentoPageProvider(props: IAtendimentoPageProviderProps) {
    const [Atendimento, setAtendimento] = useState<IAtendimento>();
    const [listenDocChanges, setListenDocChanges] = useState(true);
    const [loading, setLoading] = useState(true);
    const [QtdOutrosProdutosInadimplentes, setQtdOutrosProdutosInadimplentes] =
        useState(0);

    const DS_MODULO = useModuloInfo().DS_MODULO as IAtendimentoModulos;

    const bloquearInteracoes = !!Atendimento?.DT_FIM;

    const updateAtendimentoEffect = (
        TIPO_CASO: IAtendimentoModulos,
        ID_CASO: number
    ) => {
        CASO[TIPO_CASO].getCaso(ID_CASO)
            .then(setAtendimento)
            .finally(() => setLoading(false));
    };
    const updateAtendimento = () => {
        updateAtendimentoEffect(DS_MODULO, props.ID_CASO);
    };

    const updateDoc = useCallback(() => {
        setListenDocChanges((currentState) => !currentState);
    }, []);

    const { value: lstInforVendas, execute: getInforVendas } =
        useAsyncEffect(async () => {
            if (
                props.ID_CASO &&
                ACESSOS.VENDAS.ID_MODULO === Atendimento?.ID_TIPO_CASO
            ) {
                return getInforVendasController(props.ID_CASO);
            }
        }, [props.ID_CASO, ACESSOS.VENDAS.ID_MODULO, Atendimento]);

    const {
        value: Cliente,
        loading: ClienteLoading,
        execute: GetCliente,
    } = useAsyncEffect(async () => {
        if (Atendimento?.ID_CONTA)
            return GetClienteCompleto(Atendimento?.ID_CONTA);
    }, [Atendimento?.ID_CONTA]);

    const { value: dadosOportunidade, loading: dadosOportunidadeLoading } =
        useAsyncEffect(async () => {
            if (
                Atendimento?.ID_OPORTUNIDADE &&
                ACESSOS.VENDAS.ID_MODULO === Atendimento?.ID_TIPO_CASO
            )
                return DadosOportunidadeLeadController(
                    Atendimento?.ID_OPORTUNIDADE || 0
                );
        }, [Atendimento]);

    useEffect(() => {
        setLoading(true);
        updateAtendimentoEffect(DS_MODULO, props.ID_CASO);
    }, [DS_MODULO, props.ID_CASO]);

    return (
        <AtendimentoPageContext.Provider
            value={{
                listenDocChanges,
                Atendimento,
                updateAtendimento,
                updateDoc,
                QtdOutrosProdutosInadimplentes,
                setQtdOutrosProdutosInadimplentes,
                bloquearInteracoes,
                TIPO_CASO: DS_MODULO,
                idModulo: CASO[DS_MODULO].id,
                ID_CASO: props.ID_CASO,
                lstInforVendas,
                getInforVendas,
                Cliente,
                ClienteLoading,
                GetCliente,

                dadosOportunidade,
                dadosOportunidadeLoading,
            }}
        >
            {loading ? (
                <SpinnerComponent />
            ) : Atendimento ? (
                <HistoricoProvider
                    ID_CONTA={Atendimento?.ID_CONTA}
                    ID_CASO={props.ID_CASO}
                >
                    {props.children}
                </HistoricoProvider>
            ) : (
                <InternalError />
            )}
        </AtendimentoPageContext.Provider>
    );
}

export function useAtendimentoPage() {
    const context = useContext(AtendimentoPageContext);
    if (!context)
        throw new Error(
            "useAtendimentoPage necessita de um AtendimentoPageProvider em volta do que sera utilizado"
        );

    return context;
}
